/**
 * GapAnalysisPanel - Gap analysis view for smart sets
 * IMPL-FEATURE2-SMARTSETS-20260204
 *
 * Shows missing issues for a smart set with MDM inventory cross-reference.
 * Allows adding gaps to want list.
 *
 * ⛔ VAULT-S4 V-SMART-FE-FIELDS: this panel read `issue_id` / `is_key` / `in_mdm_inventory` /
 * `mdm_price` -- NONE of which GET /vault/smart-sets/{id}/gaps has ever served. GapItem
 * (schemas/smart_sets.py) is `{id, comic_issue_id, issue_number, series_name, cover_date,
 * cover_url, is_key_issue, key_reason, estimated_value, in_stock_at_mdm, mdm_product_id}`. So
 * every card's key was `undefined`, selection toggled one shared `undefined` entry, the Key
 * badge never rendered, and "Add to Want List" posted `[undefined]`. The suite was green
 * because its fixtures were hand-typed in the same wrong shape. Every read below is a served
 * name; the fixtures are generated from the OpenAPI snapshot.
 *
 * The MDM product affordance is gated on `in_stock_at_mdm && mdm_product_id`. The comic
 * catalog is NEVER sold (commerce is SaaS + BCW supplies), the compute service writes
 * `in_stock_at_mdm=false` / `mdm_product_id=null` by decision, and the SPA has no
 * `/product/:id` route (supply products are keyed by sku at /maestro/supply-lab/:mdmSku) --
 * so the pre-S4 `window.open('/product/…')` was a dead link even when it could fire. A gap
 * that is not a purchasable product is a catalog issue: its affordance is the want list.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  ShoppingCart, Heart, Star,
  Loader2, AlertCircle, Package, BookOpen
} from 'lucide-react';
import { getSmartSetGaps, addGapsToWantList } from '../../services/vaultApi';
import CompletionBar from './CompletionBar';

/** Page size for the panel: the route caps page_size at 200. */
export const GAP_PAGE_SIZE = 100;

/**
 * Whether a gap carries a purchasable MDM product. Both halves are required: a stock flag
 * with no product id is nothing a buyer can act on, and a product id with the flag off is
 * out of stock.
 */
export function isPurchasableGap(gap) {
  return Boolean(gap.in_stock_at_mdm && gap.mdm_product_id);
}

/**
 * Set-level completion for the header. The gaps response's `completion` (CompletionStats,
 * never narrowed by the list filters) is the fresh read; before it arrives the smart-set row
 * the caller already holds (SmartSetResponse: completion_pct / owned_count / total_in_set)
 * paints the same bar. Absent fields render as 0, never NaN.
 */
export function completionOf(smartSet, completion) {
  const source = completion || smartSet || {};
  return {
    completion_pct: source.completion_pct ?? 0,
    owned_count: source.owned_count ?? 0,
    total_in_set: source.total_in_set ?? 0,
  };
}

function formatEstimate(value) {
  return `Est. $${Number(value).toFixed(2)}`;
}

function GapItemCard({ gap, isSelected, onSelect }) {
  const purchasable = isPurchasableGap(gap);
  return (
    <div
      data-testid={`gap-card-${gap.id}`}
      className={`p-3 rounded-lg border transition-colors cursor-pointer ${
        isSelected
          ? 'bg-orange-500/10 border-orange-500/40'
          : 'bg-zinc-900 border-zinc-800 hover:border-zinc-700'
      }`}
      onClick={() => onSelect(gap.comic_issue_id)}
    >
      <div className="flex items-start gap-3">
        {/* Cover thumbnail */}
        <div className="w-12 h-16 bg-zinc-800 rounded overflow-hidden flex-shrink-0">
          {gap.cover_url ? (
            <img
              src={gap.cover_url}
              alt={`Issue #${gap.issue_number}`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="w-5 h-5 text-zinc-600" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white">#{gap.issue_number}</span>
            {gap.is_key_issue && (
              <span className="flex items-center gap-0.5 text-[10px] bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded-full">
                <Star className="w-2.5 h-2.5" />
                Key
              </span>
            )}
          </div>

          {gap.series_name && (
            <p className="text-[11px] text-zinc-400 mt-0.5 truncate">{gap.series_name}</p>
          )}

          {gap.key_reason && (
            <p className="text-[10px] text-yellow-400/70 mt-0.5 truncate">{gap.key_reason}</p>
          )}

          {gap.cover_date && (
            <p className="text-[10px] text-zinc-500 mt-0.5">{gap.cover_date}</p>
          )}

          {gap.estimated_value != null && (
            <p className="text-[10px] text-zinc-400 mt-0.5" data-testid={`gap-estimate-${gap.id}`}>
              {formatEstimate(gap.estimated_value)}
            </p>
          )}

          {/* MDM product affordance: only a purchasable product; the catalog is never sold */}
          {purchasable ? (
            <span
              data-testid={`gap-in-stock-${gap.id}`}
              className="flex items-center gap-1 mt-1.5 text-[11px] text-green-400"
            >
              <ShoppingCart className="w-3 h-3" />
              <span>In stock at MDM</span>
            </span>
          ) : (
            <span
              data-testid={`gap-catalog-${gap.id}`}
              className="flex items-center gap-1 mt-1.5 text-[10px] text-zinc-600"
            >
              <BookOpen className="w-3 h-3" />
              <span>Catalog issue - select to add to your want list</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GapAnalysisPanel({ smartSet, onClose }) {
  const [gaps, setGaps] = useState([]);
  const [completion, setCompletion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGaps, setSelectedGaps] = useState(new Set());
  const [filter, setFilter] = useState('all'); // all, keys, in-stock
  const [isAddingToWant, setIsAddingToWant] = useState(false);

  const loadGaps = useCallback(async () => {
    if (!smartSet?.id) return;
    setIsLoading(true);
    setError(null);

    try {
      // SmartSetGapResponse: { gaps, completion, total_gaps, page, page_size }
      const result = await getSmartSetGaps(smartSet.id, {
        keys_only: filter === 'keys',
        in_stock_only: filter === 'in-stock',
        page: 1,
        page_size: GAP_PAGE_SIZE,
      });
      setGaps(result.gaps);
      setCompletion(result.completion);
    } catch (err) {
      setError(err.message || 'Failed to load gaps');
    }
    setIsLoading(false);
  }, [smartSet?.id, filter]);

  useEffect(() => {
    loadGaps();
  }, [loadGaps]);

  const handleSelect = (comicIssueId) => {
    setSelectedGaps(prev => {
      const next = new Set(prev);
      if (next.has(comicIssueId)) {
        next.delete(comicIssueId);
      } else {
        next.add(comicIssueId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedGaps.size === gaps.length) {
      setSelectedGaps(new Set());
    } else {
      setSelectedGaps(new Set(gaps.map(g => g.comic_issue_id)));
    }
  };

  // Only reachable from the bulk bar, which renders only while selectedGaps.size > 0.
  const handleAddToWantList = async () => {
    setIsAddingToWant(true);
    try {
      await addGapsToWantList(smartSet.id, Array.from(selectedGaps));
      setSelectedGaps(new Set());
      loadGaps();
    } catch (err) {
      console.error('Failed to add to want list:', err);
    }
    setIsAddingToWant(false);
  };

  // Tab counts are the SET-LEVEL CompletionStats (gap_count / key_count / gaps_in_stock),
  // never the current page: under the Keys filter the page holds only keys, so counting the
  // page would report "All (n keys)".
  const gapCount = completion?.gap_count ?? 0;
  const keyCount = completion?.key_count ?? 0;
  const inStockCount = completion?.gaps_in_stock ?? 0;
  const bar = completionOf(smartSet, completion);

  return (
    <div className="flex-1 flex flex-col bg-zinc-950 border-l border-zinc-800">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-white">Gap Analysis</h3>
          {onClose && (
            <button
              onClick={onClose}
              className="text-zinc-500 hover:text-white text-sm"
            >
              Close
            </button>
          )}
        </div>

        {smartSet && (
          <div className="mb-3">
            <p className="text-sm text-zinc-400 mb-1">{smartSet.name}</p>
            <CompletionBar
              percentage={bar.completion_pct}
              owned={bar.owned_count}
              total={bar.total_in_set}
              size="md"
            />
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-1 bg-zinc-900 rounded-lg p-1">
          {[
            { key: 'all', label: `All (${gapCount})` },
            { key: 'keys', label: `Keys (${keyCount})` },
            { key: 'in-stock', label: `In Stock (${inStockCount})` },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                filter === tab.key
                  ? 'bg-zinc-800 text-white'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedGaps.size > 0 && (
        <div className="px-4 py-2 border-b border-zinc-800 bg-zinc-900/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={handleSelectAll}
              className="text-xs text-zinc-400 hover:text-white"
            >
              {selectedGaps.size === gaps.length ? 'Deselect All' : 'Select All'}
            </button>
            <span className="text-xs text-zinc-500">
              {selectedGaps.size} selected
            </span>
          </div>
          <button
            onClick={handleAddToWantList}
            disabled={isAddingToWant}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-700 text-white text-xs rounded-lg transition-colors"
          >
            <Heart className="w-3 h-3" />
            {isAddingToWant ? 'Adding...' : 'Add to Want List'}
          </button>
        </div>
      )}

      {/* Gap List */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="w-8 h-8 text-red-400 mb-2" />
            <p className="text-sm text-red-400">{error}</p>
            <button
              onClick={loadGaps}
              className="mt-2 text-xs text-orange-400 hover:text-orange-300"
            >
              Retry
            </button>
          </div>
        ) : gaps.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="w-8 h-8 text-green-400 mb-2" />
            <p className="text-sm text-green-400 font-medium">
              {filter === 'all' ? 'Set Complete!' : 'No gaps match this filter'}
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              {filter === 'all' ? 'No gaps found for this smart set.' : 'Every gap in this set is outside the filter.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {gaps.map(gap => (
              <GapItemCard
                key={gap.id}
                gap={gap}
                isSelected={selectedGaps.has(gap.comic_issue_id)}
                onSelect={handleSelect}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
